import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { debounceTime, filter } from 'rxjs/operators';
import { ItemsPanelsComponent } from './components/items-panels/items-panels.component';
import { ItemsSelector } from './helpers/items-selector';

@Directive({
  selector: '[itemsSelector]',
})
export class ItemsSelectorDirective implements OnInit {
  @Input() itemsSelector!: ItemsSelector;
  @Input() searchInputPlaceholder: string = 'Search';
  @Input() noItemFoundText: string = 'No item found';
  @Input() autoFocusInput = true;
  @Input() closeAfterSelection = true;

  @Output() selectorOpened = new EventEmitter<void>();

  private itemsPanelOverlayRef!: OverlayRef;
  private clickListener!: () => void;

  constructor(
    private cdr: ChangeDetectorRef,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private triggerElement: ElementRef
  ) {}

  ngOnInit(): void {
    if (!this.itemsSelector) {
      throw new Error('Please provide itemsSlelector input');
    }
    this.itemsSelector.newItem.subscribe(() => this.close());

    // Automatically close in the case of a single selection
    this.itemsSelector.selected
      .pipe(
        filter((selected) => !!selected),
        debounceTime(500)
      )
      .subscribe(() => {
        if (!this.itemsSelector.multipleSelection && this.closeAfterSelection) {
          this.close();
        }
      });

    this.clickListener = this.renderer.listen(
      this.triggerElement.nativeElement,
      'click',
      () => this.open()
    );
  }

  ngOnDestroy(): void {
    this.clickListener();
  }

  open(): void {
    if (this.itemsSelector.disableSelector) return;
    // Create the overlay
    this.itemsPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.triggerElement.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    // Subscribe to the attachments observable
    this.itemsPanelOverlayRef.attachments().subscribe(() => {
      // Add a class to the origin
      this.renderer.addClass(this.triggerElement.nativeElement, 'panel-opened');

      // Focus to the search input once the overlay has been attached
      if (this.autoFocusInput) {
        setTimeout(() => {
          this.itemsPanelOverlayRef.overlayElement
            .querySelector('input')!
            .focus();
        });
      }

      // Announce the opening
      this.selectorOpened.emit();
      this.cdr.markForCheck();
    });

    // Create a portal from the component
    const componentPortal = new ComponentPortal(
      ItemsPanelsComponent,
      this.viewContainerRef
    );

    // Attach the portal to the overlay
    const panelRef = this.itemsPanelOverlayRef.attach(componentPortal);
    // Set items panel components inputs value
    panelRef.instance.itemsSelector = this.itemsSelector;
    panelRef.instance.searchInputPlaceholder = this.searchInputPlaceholder;
    panelRef.instance.noItemFoundText = this.noItemFoundText;

    // Subscribe to the backdrop click
    this.itemsPanelOverlayRef.backdropClick().subscribe(() => {
      // Remove the class from the origin
      this.renderer.removeClass(
        this.triggerElement.nativeElement,
        'panel-opened'
      );

      // If overlay exists and attached...
      if (this.itemsPanelOverlayRef?.hasAttached()) {
        // Detach it
        this.itemsPanelOverlayRef.detach();
      }

      // If template portal exists and attached...
      if (componentPortal?.isAttached) {
        // Detach it
        componentPortal.detach();
      }
    });
  }

  close() {
    this.itemsPanelOverlayRef?.backdropElement?.click();
  }
}
